import React, { useEffect, useState } from 'react';
import { ArrowRight, Shield, Clock, PoundSterling, CheckCircle, Building2, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import BuckyballBackground from '../BuckyballBackground';
import CertificationBadges from '../certifications/CertificationBadges';

export default function UnifiedHero() {
  const [animationQuality, setAnimationQuality] = useState<'low' | 'medium' | 'high'>('high');

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isLowEndDevice = typeof navigator.deviceMemory !== 'undefined' ?
      navigator.deviceMemory < 4 : false;

    if (isMobile && isLowEndDevice) {
      setAnimationQuality('low');
    } else if (isMobile) {
      setAnimationQuality('medium');
    }
  }, []);

  return (
    <div className="relative">
      <div className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900" />
        <BuckyballBackground quality={animationQuality} />

        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/50 to-transparent" />

        <div className="relative w-full px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">G-Cloud 14 • Public Sector Ready</span>
              </div>
              <div className="inline-flex items-center space-x-2 bg-teal-500/10 border border-teal-500/30 rounded-full px-4 py-2">
                <Briefcase className="w-4 h-4 text-teal-400" />
                <span className="text-teal-400 text-sm font-medium">Enterprise Solutions</span>
              </div>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="text-white [text-shadow:_0_1px_12px_rgb(0_0_0_/_50%)]">
                Intelligent AI Automation
              </span>
              <br />
              <span className="text-gradient relative z-10 [text-shadow:_0_1px_12px_rgb(0_168_204_/_20%)]">
                for Government & Enterprise
              </span>
            </h1>

            <p className="text-xl text-gray-300 max-w-4xl mb-8 [text-shadow:_0_1px_12px_rgb(0_0_0_/_50%)]">
              Transform operations with proven AI automation delivering 15:1 ROI.
              Trusted by UK government departments, NHS trusts, councils, and leading enterprises.
              UK-based, secure, and compliant.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-teal-500/20 p-2 rounded-lg">
                    <PoundSterling className="w-6 h-6 text-teal-400" />
                  </div>
                  <h3 className="text-white font-semibold">Proven ROI</h3>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• 15:1 average return</li>
                  <li>• 60-90% time savings</li>
                  <li>• 2-3 month payback</li>
                </ul>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-blue-500/20 p-2 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-white font-semibold">Rapid Deployment</h3>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• 4-6 week delivery</li>
                  <li>• Pilot in 2-4 weeks</li>
                  <li>• Zero downtime</li>
                </ul>
              </div>

              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="bg-green-500/20 p-2 rounded-lg">
                    <Shield className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-white font-semibold">Enterprise Security</h3>
                </div>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• UK data residency</li>
                  <li>• Cyber Essentials Plus</li>
                  <li>• ISO 27001 certified</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-6 mb-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">Public Sector</h3>
                  </div>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span>G-Cloud 14 framework access</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span>Direct awards up to £10M</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span>Social value commitments</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span>ATRS compliance for AI systems</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <Briefcase className="w-5 h-5 text-teal-400" />
                    <h3 className="text-lg font-semibold text-white">Private Sector</h3>
                  </div>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      <span>Scalable enterprise solutions</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      <span>Custom workflow automation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      <span>24/7 support and monitoring</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-teal-400 flex-shrink-0" />
                      <span>Flexible pricing models</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Book Free Consultation
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>

              <Link
                to="/frameworks"
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Shield className="w-5 h-5 mr-2" />
                G-Cloud Services
              </Link>

              <Link
                to="/tools/roi-calculator"
                className="group inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-gray-300 border-2 border-gray-600 hover:border-gray-500 hover:text-white transition-all duration-200"
              >
                Calculate ROI
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>No upfront investment required</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>Free pilot available</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span>UK-based support team</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CertificationBadges />
    </div>
  );
}
